import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { agentSelfApi, type MyAgentProfile, type MyBankChange } from '../api/agentSelf'
import { Button, Callout, Card, CardContent, CardHeader, LoadingState, Page, PageHeader, StatusPill, TextField } from '../components/ui'

const STATUS_LABEL: Record<MyBankChange['status'], string> = {
  pending: 'In attesa di approvazione',
  approved: 'Approvata',
  rejected: 'Rifiutata',
}

/**
 * R3-28 (REQ-260-001): il profilo dell'agente. L'IBAN si vede sempre mascherato; per cambiarlo l'agente invia una
 * richiesta che l'Amministrazione approva. Le provvigioni sono nella pagina Provvigioni (mostra solo le proprie righe).
 * Margine e costo del lead non esistono in questa pagina. Flag `agenti.profilo_self`.
 */
export function MyAgentProfilePage() {
  const [profile, setProfile] = useState<MyAgentProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [iban, setIban] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setProfile(await agentSelfApi.me())
    } catch (e) {
      setProfile(null)
      setError(e instanceof Error ? e.message : 'Profilo non disponibile')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setSent(false)
    try {
      await agentSelfApi.requestIbanChange(iban.trim())
      setIban('')
      setSent(true)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Richiesta non inviata')
    } finally {
      setBusy(false)
    }
  }

  const last = profile?.lastBankChange ?? null

  return (
    <Page>
      <PageHeader title="Il mio profilo agente" icon="badge" />
      {loading && <LoadingState />}
      {!loading && !profile && <Callout tone="warning">{error || 'Profilo agente non disponibile.'}</Callout>}
      {profile && (
        <>
          <Card variant="outlined">
            <CardHeader title="Dati" />
            <CardContent>
              <p>Codice agente: <strong>{profile.code ?? '—'}</strong></p>
              <p>IBAN: <strong>{profile.ibanMasked ?? 'non indicato'}</strong></p>
              <p><Link to="/commissions">Vedi le mie provvigioni</Link></p>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader title="Modifica dei dati bancari" />
            <CardContent>
              {profile.pendingBankChange ? (
                <Callout tone="info" icon="hourglass_top">
                  Richiesta inviata il {new Date(profile.pendingBankChange.requestedAt).toLocaleDateString('it-IT')} per
                  l'IBAN {profile.pendingBankChange.newIbanMasked}: in attesa dell'Amministrazione. Inviandone un'altra
                  sostituisci questa.
                </Callout>
              ) : last && (
                <Callout tone={last.status === 'approved' ? 'success' : 'warning'}>
                  Ultima richiesta: <StatusPill tone={last.status === 'approved' ? 'success' : 'error'}>{STATUS_LABEL[last.status]}</StatusPill>
                  {last.decisionNote && <> — {last.decisionNote}</>}
                </Callout>
              )}
              {error && <Callout tone="error">{error}</Callout>}
              {sent && <Callout tone="success">Richiesta inviata all'Amministrazione.</Callout>}
              <form onSubmit={e => void submit(e)} className="md-stack">
                <TextField label="Nuovo IBAN" value={iban} onChange={e => setIban(e.target.value)} autoComplete="off" />
                <div>
                  <Button type="submit" variant="filled" disabled={busy || iban.trim().length === 0}>Invia richiesta</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </Page>
  )
}
